import { Link, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import type { MatchProfile, Profile } from "../../types";
import { buildPhotoViewerUrl } from "./photoViewerUtils";
import { ProtectedImage } from "./ProtectedImage";
import { ProfilePhoto } from "./ProfilePhoto";
import { formatLocationLine } from "./locationUtils";
import { ProfileDetails } from "./ProfileDetails";
import {
  scrollProfileCardToTop,
  updateProfileCollapse,
  updateProfileRegionScroll,
  useProfileScrollAffordance,
} from "./profileCardScroll";
import {
  profileGalleryPhotoItems,
  profilePhotoItems,
  profilePicturePhotoId,
} from "./profilePhotoPresentation";

function profileGalleryItems(profile: MatchProfile | Profile, showGallery = false) {
  const allPhotos = showGallery ? profilePhotoItems(profile) : [];
  const photos = profileGalleryPhotoItems(profile, allPhotos);

  if (showGallery && !allPhotos.length) {
    return Array.from({ length: 10 }, (_, index) => ({
      id: `preview-photo-${index + 1}`,
      index,
      isPreview: true,
    }));
  }

  return photos.map((photo, index) => ({
    id: photo.id || `photo-${index}`,
    index,
    isPreview: false,
  }));
}

function ProfileAvatar({ profile, showGallery = false }: { profile: MatchProfile | Profile; showGallery?: boolean }) {
  const photos = showGallery ? profilePhotoItems(profile) : [];
  const avatarPhotoId = profilePicturePhotoId(profile, photos);

  return (
    <div className="profile-hero-avatar" aria-label={avatarPhotoId ? `${profile.firstName} profile photo` : `${profile.firstName} profile placeholder`}>
      <ProfilePhoto photoId={avatarPhotoId} variant="full" loading="eager" />
    </div>
  );
}

function ProfileHero({ profile, showGallery = false }: { profile: MatchProfile | Profile; showGallery?: boolean }) {
  const location = useLocation();
  const photos = profileGalleryItems(profile, showGallery);
  const viewerPhotos = photos.filter((photo) => !photo.isPreview).map((photo) => photo.id);
  const photoCountClass = `photo-count-${Math.min(photos.length, 10)}`;
  const photoViewerState = { from: location.pathname };

  return (
    <div className={`profile-hero ${photos.length ? "has-gallery" : "is-placeholder"} ${photos.length > 1 ? "has-mosaic" : ""} ${photoCountClass}`}>
      <div className="profile-hero-gallery" aria-label={photos.length ? `${profile.firstName}'s photos` : undefined}>
        {photos.length ? photos.map((photo) => (
          photo.isPreview ? (
            <div className="profile-hero-slide profile-photo-preview" data-photo-index={photo.index + 1} key={photo.id} aria-hidden="true" />
          ) : (
            <Link to={buildPhotoViewerUrl(viewerPhotos, photo.id)} state={photoViewerState} className="profile-hero-slide" key={photo.id}>
              <ProtectedImage
                photoId={photo.id}
                variant={photo.index === 0 ? "full" : "thumbnail"}
                loading={photo.index === 0 ? "eager" : "lazy"}
                fetchPriority={photo.index === 0 ? "high" : "auto"}
                alt={`${profile.firstName} photo ${photo.index + 1}`}
              />
            </Link>
          )
        )) : <div className="profile-hero-placeholder" aria-hidden="true" />}
      </div>
    </div>
  );
}

function ProfileDesktopPhotoGrid({ profile, showGallery = false }: { profile: MatchProfile | Profile; showGallery?: boolean }) {
  const location = useLocation();
  const galleryPhotos = profileGalleryItems(profile, showGallery);
  const viewerPhotos = galleryPhotos.filter((photo) => !photo.isPreview).map((photo) => photo.id);
  const photos = galleryPhotos.slice(1, 10);
  const photoViewerState = { from: location.pathname };

  if (!photos.length) return null;

  return (
    <div className={`profile-desktop-photo-grid photo-count-${Math.min(photos.length, 9)}`} aria-label={`${profile.firstName}'s additional photos`}>
      {photos.map((photo) => (
        photo.isPreview ? (
          <div className="profile-desktop-photo-tile profile-photo-preview" data-photo-index={photo.index + 1} key={photo.id} aria-hidden="true" />
        ) : (
          <Link to={buildPhotoViewerUrl(viewerPhotos, photo.id)} state={photoViewerState} className="profile-desktop-photo-tile" key={photo.id}>
            <ProtectedImage photoId={photo.id} alt={`${profile.firstName} photo ${photo.index + 1}`} />
          </Link>
        )
      ))}
    </div>
  );
}

export function MatchProfileCard({
  profile,
  kicker,
  subtitle,
  showGallery = true,
  connected = true,
  className = "",
  notice,
  children,
}: {
  profile: MatchProfile;
  kicker: string;
  subtitle?: ReactNode;
  showGallery?: boolean;
  connected?: boolean;
  className?: string;
  notice?: ReactNode;
  children?: ReactNode;
}) {
  const profileCardRef = useProfileScrollAffordance();
  const locationLine = formatLocationLine(profile.city, profile.state, profile.country);

  return (
    <article
      ref={profileCardRef}
      className={`profile-card match-profile-card ${connected ? "connected" : "pending"} ${className}`.trim()}
      onScroll={updateProfileCollapse}
    >
      <ProfileHero profile={profile} showGallery={showGallery} />
      <div className={`profile-title ${connected ? "connected" : ""}`} onClick={scrollProfileCardToTop}>
        <ProfileAvatar profile={profile} showGallery={showGallery} />
        <div className="profile-title-copy">
          <span className="profile-card-kicker">{kicker}</span>
          <h2>{profile.firstName}, {profile.age}</h2>
          {profile.occupation ? <p className="profile-title-occupation">{profile.occupation}</p> : null}
          {subtitle ? (
            <div className="profile-title-subtitle">{subtitle}</div>
          ) : (
            <p>{locationLine}</p>
          )}
        </div>
      </div>
      {showGallery && <ProfileDesktopPhotoGrid profile={profile} showGallery />}
      <div className={`profile-scroll-region ${connected ? "profile-scroll-region--connected" : ""}`} onScroll={updateProfileRegionScroll}>
        {notice}
        {profile.aboutMe && (
          <section>
            <h3>About</h3>
            <p>{profile.aboutMe}</p>
          </section>
        )}
        {profile.lookingFor && (
          <section>
            <h3>Looking For</h3>
            <p>{profile.lookingFor}</p>
          </section>
        )}
        <ProfileDetails profile={profile} />
        {children}
      </div>
    </article>
  );
}
